"use client";

import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import BackgroundAnimation from '../BackgroundAnimation';

interface CarouselItem {
  id: string;
  title: string;
  description: string;
  image?: string;
  category?: string;
  date?: string;
  readTime?: string;
  tags?: string[];
  technologies?: string[];
  github?: string;
  url?: string;
  type: 'project' | 'blog';
}

interface HorizontalCarouselProps {
  items: CarouselItem[];
  title: string;
  subtitle?: string;
}

export const HorizontalCarousel: React.FC<HorizontalCarouselProps> = ({ 
  items, 
  title, 
  subtitle 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const carouselRef = useRef<HTMLDivElement>(null);

  const checkScrollability = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      const hasOverflow = scrollWidth > clientWidth;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
      
      // Hide arrows if there's no overflow
      if (!hasOverflow) {
        setCanScrollLeft(false);
        setCanScrollRight(false);
      }
    }
  };

  const scrollLeft = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', checkScrollability);
      checkScrollability();
      return () => carousel.removeEventListener('scroll', checkScrollability);
    }
  }, []);

  return (
    <section className="w-full bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900 py-20 relative overflow-hidden">
      {/* Animated Background */}
      <BackgroundAnimation variant="waves" intensity="subtle" />
      
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-10 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-dark-900 dark:text-white mb-4">
            {title}
          </h2>
          {subtitle && (
            <p className="text-xl text-dark-600 dark:text-dark-300 max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </motion.div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Arrows */}
          {canScrollLeft && (
            <button
              onClick={scrollLeft}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-300 flex items-center justify-center"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
          )}
          
          {canScrollRight && (
            <button
              onClick={scrollRight}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-300 flex items-center justify-center"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          )}

          {/* Carousel */}
          <div
            ref={carouselRef}
            className="flex gap-6 overflow-x-auto scroll-smooth py-10 [scrollbar-width:none] -mx-4 px-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            
            {items.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex-shrink-0 w-[85vw] sm:w-80 md:w-96 h-[26rem] sm:h-[28rem] md:h-[32rem] rounded-3xl overflow-hidden relative group cursor-pointer border border-white/20 shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
                onClick={() => {
                  // Navigate to the appropriate URL
                  if (item.type === 'blog' && item.url) {
                    window.open(item.url, '_blank');
                  } else if (item.type === 'project' && item.github) {
                    window.open(item.github, '_blank');
                  }
                }}
              >
                {/* Background Image */}
                <div className="absolute inset-0">
                  {item.image ? (
                    <div className="relative w-full h-full overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary-600 to-purple-600" />
                  )}
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                </div>

                {/* Content Overlay */}
                <div className="absolute inset-0 p-6 sm:p-8 flex flex-col justify-end">
                  {/* Additional gradient overlay for text area */}
                  <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-black/95 via-black/70 to-transparent rounded-b-3xl"></div>
                  <div className="relative z-10 text-white">
                    {/* Category */}
                    {item.category && (
                      <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-sm font-medium mb-2 opacity-90"
                      >
                        {item.category}
                      </motion.p>
                    )}

                    {/* Title */}
                    <motion.h3
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-2xl md:text-3xl font-bold mb-3 leading-tight"
                    >
                      {item.title}
                    </motion.h3>

                    {/* Description */}
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="text-white/80 text-sm md:text-base mb-4 line-clamp-2"
                    >
                      {item.description}
                    </motion.p>

                    {/* Meta Information */}
                    {(item.date || item.readTime) && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="flex items-center space-x-4 text-white/60 text-sm mb-4"
                      >
                        {item.date && (
                          <span>
                            {new Date(item.date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        )}
                        {item.readTime && (
                          <span>{item.readTime}</span>
                        )}
                      </motion.div>
                    )}

                    {/* Tags/Technologies */}
                    {(item.tags?.length || item.technologies?.length) && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="flex flex-wrap gap-2"
                      >
                                                 {(item.type === 'project' ? item.technologies : item.tags)?.slice(0, 3).map((tag, idx) => (
                           <span
                             key={idx}
                             className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium"
                           >
                             {tag}
                           </span>
                         ))}
                         {((item.type === 'project' ? item.technologies : item.tags)?.length || 0) > 3 && (
                           <span className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium">
                             +{((item.type === 'project' ? item.technologies : item.tags)?.length || 0) - 3}
                           </span>
                         )}
                      </motion.div>
                    )}
                  </div>
                </div>

                {/* Hover Effect */}
                <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}; 